import { useLazyQuery } from '@apollo/client'
import { useEffect } from 'react'

import { GetAiScanningEmailAddressesDocument } from '@/graphql/purchase-plus/generated/getAiScanningEmailAddresses.generated'
import {
  AiScanningEmailAddress,
  AiScanningEmailAddressesQuerySortsPropertyEnum,
  SortDirectionEnum,
} from '@/graphql/purchase-plus/generated/purchasePlus_graphql'
import { PURCHASE_PLUS_GRAPHQL_API } from '@/modules/shared/constants'
import { useWatchQueryParams } from '@/modules/shared/hooks/useWatchQueryParams'
import { checkNetworkStatus } from '@/modules/shared/utils/checkNetworkStatus'
import { extractEdges } from '@/modules/shared/utils/extractEdges'
import { onFetchMore, onNextPage, onPreviousPage } from '@/modules/shared/utils/paginationUtils'

interface UseGetAiScanningEmailAddressesProps {
  ownerIdProp?: number
  lazyQuery?: boolean
}

export default function useGetAiScanningEmailAddresses(args?: UseGetAiScanningEmailAddressesProps) {
  const { ownerIdProp, lazyQuery } = args || {}
  const paginationResultsPerPage = 20
  const { searchValue, ownerId } = useWatchQueryParams()

  const [fetchAiScanningEmailAddresses, { data, error, networkStatus, fetchMore, refetch }] = useLazyQuery(
    GetAiScanningEmailAddressesDocument,
    {
      variables: {
        first: paginationResultsPerPage,
        filters: {
          email: searchValue,
          ownerId: ownerIdProp || Number(ownerId) || undefined,
        },
        sorts: [
          {
            direction: SortDirectionEnum.Desc,
            property: AiScanningEmailAddressesQuerySortsPropertyEnum.CreatedAt,
          },
        ],
      },
      context: { uri: PURCHASE_PLUS_GRAPHQL_API },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
    }
  )

  useEffect(() => {
    if (!lazyQuery) fetchAiScanningEmailAddresses()
  }, [])

  const pageInfo = data?.aiScanningEmailAddresses?.pageInfo

  return {
    fetchAiScanningEmailAddresses,
    emailAddresses: extractEdges<AiScanningEmailAddress>(data?.aiScanningEmailAddresses),
    error,
    paginationResultsPerPage,
    loadingStates: checkNetworkStatus(networkStatus),
    onFetchMoreOrganisations: () =>
      onFetchMore({
        endCursor: pageInfo?.endCursor || null,
        fetchMore,
        paginationResultsPerPage,
      }),
    onNextPage: () =>
      onNextPage({
        endCursor: pageInfo?.endCursor || null,
        refetch,
        paginationResultsPerPage,
      }),
    onPreviousPage: () =>
      onPreviousPage({
        startCursor: pageInfo?.startCursor || null,
        refetch,
        paginationResultsPerPage,
      }),
    pageInfo,
    networkStatus,
    refetch,
  }
}
